import React, { memo } from 'react';
import {
    makeStyles,
    createStyles,
    Theme,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Divider,
    Box,
    withStyles,
    Typography
} from '@material-ui/core';
import { TableCellProps } from '@material-ui/core/TableCell';
import { WithStyles, StyleRules, ClassNameMap } from '@material-ui/core/styles/withStyles';
import { DeepReadonly } from 'utility-types';

import { PayrollTicket } from './types';
import { Icon } from '../../icons';

const useStyles = makeStyles(
    (theme: Theme): StyleRules<TableRowClassKey> =>
        createStyles({
            root: {},
            listRoot: {
                marginTop: theme.spacing(2),
                borderRadius: '0px',
                boxShadow: 'none'
            },
            table: {},
            /*  tableCellRoot: {
                borderBottomColor: theme.palette.listLine
            }, */
            tableCellBody: {
                color: theme.palette.text.secondary,
                padding: '0px 16px'
            } /* 
            tableCellHead: {
                color: theme.palette.text.primary
            },*/,
            tableRowAnyItem: { padding: '16px' },
            actionIconRoot: {
                '&:not(:last-of-type)': {
                    marginRight: '15px'
                }
            },
            actionIcon: {
                fontSize: '1.2rem'
            }
        })
);

type TableRowClassKey = 'root' | 'listRoot' | 'tableRowAnyItem' | 'tableCellBody' | 'table' | 'actionIconRoot' | 'actionIcon';

const extendedTableCellStyles = (theme: Theme): StyleRules<ExtendedTableCellClassKey, ExtendedTableCellStyleProps> =>
    createStyles({
        root: { padding: '0px' },
        wrapper: { display: 'flex' },
        body: {
            padding: '16px'
            /* cursor: "pointer" */
        },
        bodyActive: {
            padding: '16px',
            /* cursor: "pointer", */
            color: theme.palette.text.primary
        },
        indicatorActive: {
            width: '4px',
            backgroundColor: theme.palette.primary.main
        },
        indicatorInactive: {
            width: '4px',
            backgroundColor: 'transparent'
        }
    });

type ExtendedTableCellClassKey = 'root' | 'wrapper' | 'body' | 'bodyActive' | 'indicatorActive' | 'indicatorInactive';
type ExtendedTableCellStyleProps = {};
type ExtendedTableCellProps = {
    selected?: boolean;
    children: React.ReactNode;
} & TableCellProps &
    ExtendedTableCellStyleProps &
    WithStyles<ExtendedTableCellClassKey>;

const ExtendedTableCell: React.FunctionComponent<ExtendedTableCellProps> = props => {
    console.warn('ExtendedTableCell');
    const { selected, children, classes, ...muiTableCellProps } = props;
    const { body, bodyActive, wrapper, indicatorActive, indicatorInactive, ...muiTableCellClasses } = classes;

    return (
        <TableCell className={classes.root} {...muiTableCellProps} classes={{ ...muiTableCellClasses }}>
            <div className={classes.wrapper}>
                <Divider className={selected ? classes.indicatorActive : classes.indicatorInactive} orientation="vertical" />
                <span className={selected ? classes.bodyActive : classes.body}>{children}</span>
            </div>
        </TableCell>
    );
};

const ExtendedTableCellWithStyles = withStyles(extendedTableCellStyles)(ExtendedTableCell);

type PayrollTicketsTableProps = DeepReadonly<{
    rows: PayrollTicket[];

    downloadPayrollAction: (payrollId: number) => void;
    getPayrollPinCode: (pairollId: number) => void;
}>;

const getTableRows = (
    rows: DeepReadonly<PayrollTicket[]>,
    selected: number,
    classes: ClassNameMap<TableRowClassKey>,
    handleClick: (event: React.MouseEvent<unknown>, id: number) => void,
    handleActionDownloadPayroll: (pairollId: number) => void,
    handleActionGetPayrollPinCode: (pairollId: number) => void
): React.ReactElement[] | React.ReactElement => {
    if (rows.length === 0) {
        return (
            <TableRow key="emptyRow" className={classes.tableRowAnyItem}>
                <TableCell classes={{ body: classes.tableCellBody }} align="left" variant="head" colSpan={3}>
                    <Typography align="center">Pro vybrané období nejsou k dispozici žádné výplatní lístky.</Typography>
                </TableCell>
            </TableRow>
        );
    }

    return rows.map(row => (
        <TableRow
            key={row.name}
            /*  hover */
            onClick={(event): void => handleClick(event, row.id)}
        >
            <ExtendedTableCellWithStyles
                selected={selected === row.id ? true : false}
                classes={{ body: classes.tableCellBody }}
                align="left"
                component="th"
                scope="row"
                variant="body"
            >
                {row.period}
            </ExtendedTableCellWithStyles>
            <TableCell classes={{ body: classes.tableCellBody }} align="left" variant="body">
                {row.name}
            </TableCell>
            <TableCell classes={{ body: classes.tableCellBody }} align="left" variant="body">
                <Box display="flex" flexDirection="row" alignItems="center">
                    <Icon
                        iconName="download"
                        className={classes.actionIcon}
                        IconProps={{
                            classes: { root: classes.actionIconRoot },
                            color: 'primary',
                            title: 'Stáhnout',
                            style: { cursor: 'pointer' },
                            onClick: (): void => handleActionDownloadPayroll(row.id)
                        }}
                    />

                    <Icon
                        iconName="key"
                        className={classes.actionIcon}
                        IconProps={{
                            classes: { root: classes.actionIconRoot },
                            color: 'primary',
                            title: 'Odeslat heslo',
                            style: { cursor: 'pointer' },
                            onClick: (): void => handleActionGetPayrollPinCode(row.id)
                        }}
                    />
                </Box>
            </TableCell>
        </TableRow>
    ));
};

const PayrollTicketsTable: React.FunctionComponent<PayrollTicketsTableProps> = memo(props => {
    console.warn('PayrollTicketsTable');
    const classes = useStyles();
    const defaultSelectedValue = props.rows[0] ? props.rows[0].id : -1;
    const [selected, setSelected] = React.useState<number>(defaultSelectedValue);
    const handleClick = (event: React.MouseEvent<unknown>, id: number): void => {
        setSelected(id);
    };

    const rows = getTableRows(props.rows, selected, classes, handleClick, props.downloadPayrollAction, props.getPayrollPinCode);

    return (
        <Table className={classes.table} aria-label="payrolls table">
            <TableHead>
                <TableRow>
                    <ExtendedTableCellWithStyles>Období</ExtendedTableCellWithStyles>
                    <TableCell>Název</TableCell>
                    <TableCell>Akce</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>{rows}</TableBody>
        </Table>
    );
});

export default PayrollTicketsTable;
